import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  CheckSquare,
  AlertTriangle,
  CheckCircle,
  XCircle,
  CreditCard,
  Building2,
  RefreshCw,
  Search,
  Navigation,
  MapPin,
  Clock,
  Paperclip,
  FileText,
  Eye,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../constants/permissions';
import TravelRequestDetailsModal from './TravelRequestDetailsModal';
import AttachmentPreviewModal from '../../components/travel/AttachmentPreviewModal';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelApprovalInbox = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [outstationRequests, setOutstationRequests] = useState([]);
  const [localRequests, setLocalRequests] = useState({
    pending: [],
    approved: [],
    rejected: [],
    returned: [],
    history: [],
  });
  const [creditCards, setCreditCards] = useState([]);
  
  // Filter States
  const [activeTab, setActiveTab] = useState('pending');
  const [moduleFilter, setModuleFilter] = useState('all'); // 'all', 'outstation', 'local'
  const [searchQuery, setSearchQuery] = useState('');
  
  const [actionComments, setActionComments] = useState({});
  const [approvedAmounts, setApprovedAmounts] = useState({});
  const [assignedCardId, setAssignedCardId] = useState('');
  const [actionProcessing, setActionProcessing] = useState(null);

  // Full Details Modal State
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [selectedModuleType, setSelectedModuleType] = useState('outstation');
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const handleOpenDetails = (r) => {
    setSelectedRequestId(r._id || r.id);
    setSelectedModuleType(r.module_type || 'outstation');
    setDetailsModalOpen(true);
  };

  const handleDownloadCardPdf = async (r) => {
    try {
      toast.info('Generating PDF...');
      const isLoc = r.module_type === 'local';
      const reqId = r._id || r.id;
      const refNo = r.request_number || r.report_number || reqId;
      const endpoint = isLoc
        ? `${API_URL}/ess/local-travel/reports/${reqId}/pdf`
        : `${API_URL}/travel-desk/requests/${reqId}/pdf`;

      const res = await axios.get(endpoint, {
        responseType: 'blob',
        withCredentials: true,
      });

      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${isLoc ? 'Local_Conveyance' : 'Travel_Request'}_${refNo}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully!');
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  // Finance Advance Disbursement Form State
  const [advFormState, setAdvFormState] = useState({});
  const [disbursingId, setDisbursingId] = useState(null);

  const fetchApprovalRequests = useCallback(async () => {
    try {
      setLoading(true);
      const [outRes, locRes] = await Promise.all([
        axios.get(`${API_URL}/travel-desk/requests?view=approvals`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/local-travel/approval-inbox`, { withCredentials: true }),
      ]);
      setOutstationRequests(outRes.data.requests || []);
      setLocalRequests({
        pending: locRes.data.pending || [],
        approved: locRes.data.approved || [],
        rejected: locRes.data.rejected || [],
        returned: locRes.data.returned || [],
        history: locRes.data.history || [],
      });
    } catch (err) {
      console.error('Failed to load approval inbox:', err);
      toast.error('Failed to load approval inbox');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCreditCards = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/travel-desk/credit-cards`, { withCredentials: true });
      setCreditCards(data.credit_cards || []);
      if (data.credit_cards?.length > 0) {
        setAssignedCardId(data.credit_cards[0]._id);
      }
    } catch (err) {}
  };

  useEffect(() => {
    fetchApprovalRequests();
    fetchCreditCards();
  }, [fetchApprovalRequests]);

  const handleRemarkChange = (id, text) => {
    setActionComments((prev) => ({ ...prev, [id]: text }));
  };

  const handleApprovedAmountChange = (id, text) => {
    setApprovedAmounts((prev) => ({ ...prev, [id]: text }));
  };

  // Process Outstation Request Actions
  const handleOutstationAction = async (reqId, action, reqDoc) => {
    try {
      setActionProcessing(reqId);
      const comments = actionComments[reqId] || '';
      const payload = {
        action,
        comments,
        assigned_card_id: assignedCardId,
      };

      if (action === 'approve' && (reqDoc?.advance_requested || (reqDoc?.advance_details?.total_advance > 0))) {
        const reqAdv = reqDoc.requested_advance_amount || reqDoc.advance_details?.requested_advance_amount || reqDoc.advance_details?.total_advance || reqDoc.advance_amount || 0;
        const currentApprovedStr = approvedAmounts[reqId] !== undefined
          ? approvedAmounts[reqId]
          : (reqDoc.approved_advance_amount ?? reqDoc.advance_details?.approved_advance_amount ?? reqAdv);
        const parsedVal = parseFloat(currentApprovedStr);

        if (currentApprovedStr === '' || isNaN(parsedVal) || parsedVal < 0) {
          toast.error('Please enter a valid non-negative approved advance amount.');
          setActionProcessing(null);
          return;
        }
        payload.approved_advance_amount = parsedVal;
      }

      await axios.post(
        `${API_URL}/travel-desk/requests/${reqId}/action`,
        payload,
        { withCredentials: true }
      );
      toast.success(`Outstation request ${action}d successfully!`);
      setActionComments((prev) => ({ ...prev, [reqId]: '' }));
      fetchApprovalRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} request`);
    } finally {
      setActionProcessing(null);
    }
  };

  // Process Local Conveyance Actions
  const handleLocalAction = async (reportId, action) => {
    const comments = actionComments[reportId] || (action === 'approve' ? 'Approved' : '');
    if ((action === 'reject' || action === 'send-back') && !comments.trim()) {
      toast.error(`Please enter remarks for ${action === 'reject' ? 'rejection' : 'return'}.`);
      return;
    }
    try {
      setActionProcessing(reportId);
      const endpoint = `${API_URL}/ess/local-travel/finance/${reportId}/${action}`;
      const payload = action === 'reject'
        ? { rejection_reason: comments, remarks: comments }
        : { approval_remarks: comments, remarks: comments };

      await axios.post(endpoint, payload, { withCredentials: true });
      toast.success(`Local Conveyance claim ${action === 'send-back' ? 'returned for correction' : action + 'd'}!`);
      setActionComments((prev) => ({ ...prev, [reportId]: '' }));
      fetchApprovalRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to process ${action}`);
    } finally {
      setActionProcessing(null);
    }
  };

  const handleFinanceDisburseAdvance = async (reqId, totalAdv) => {
    const fData = advFormState[reqId] || {};
    const payment_status = fData.payment_status || 'paid';
    const paid_amount = Number(fData.paid_amount !== undefined ? fData.paid_amount : totalAdv) || 0;
    const payment_date = fData.payment_date || new Date().toISOString().split('T')[0];
    const payment_reference = fData.payment_reference || '';
    const finance_remarks = fData.finance_remarks || '';

    try {
      setDisbursingId(reqId);
      await axios.post(
        `${API_URL}/travel-desk/requests/${reqId}/advance-payment`,
        {
          payment_status,
          paid_amount,
          payment_date,
          payment_reference,
          finance_remarks,
        },
        { withCredentials: true }
      );
      toast.success(`Advance payment updated to ${payment_status.toUpperCase()}!`);
      fetchApprovalRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disburse advance');
    } finally {
      setDisbursingId(null);
    }
  };

  // Combine items into unified array
  const combinedItems = [];

  // Outstation requests
  if (moduleFilter === 'all' || moduleFilter === 'outstation') {
    outstationRequests.forEach((r) => {
      combinedItems.push({
        ...r,
        module_type: 'outstation',
        unified_id: `outstation-${r._id || r.id}`,
      });
    });
  }

  // Local Conveyance requests
  if (moduleFilter === 'all' || moduleFilter === 'local') {
    const locCategoryItems = localRequests[activeTab] || [];
    locCategoryItems.forEach((r) => {
      combinedItems.push({
        ...r,
        module_type: 'local',
        unified_id: `local-${r._id || r.id}`,
      });
    });
  }

  // Filter combined items by Active Tab & Search Query
  const filteredRequests = combinedItems.filter((r) => {
    if (r.module_type === 'outstation') {
      if (activeTab === 'pending' && !['submitted', 'pending_approval'].includes(r.status)) return false;
      if (activeTab === 'advances' && r.advance_requested !== true) return false;
      if (activeTab === 'approved' && !['approved', 'booked'].includes(r.status)) return false;
      if (activeTab === 'rejected' && r.status !== 'rejected') return false;
      if (activeTab === 'returned' && r.status !== 'returned_for_correction') return false;
    }

    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const repNum = (r.request_number || r.report_number || r.report_id || '').toLowerCase();
    const empName = (r.employee_name || r.beneficiary_name || '').toLowerCase();
    const empCode = (r.employee_code || r.beneficiary_employee_code || '').toLowerCase();
    const dept = (r.department_name || r.department || '').toLowerCase();
    const purp = (r.purpose || r.travel_purpose || '').toLowerCase();

    return repNum.includes(q) || empName.includes(q) || empCode.includes(q) || dept.includes(q) || purp.includes(q);
  });

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto font-sans" data-testid="unified-travel-approval-inbox">
      {/* Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-amber-500/20 text-amber-400 rounded-xl border border-amber-500/30 shrink-0">
            <CheckSquare className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black">Conveyance &amp; Travel Approval Inbox</h1>
            <p className="text-xs text-blue-200">
              Multi-level Local Conveyance &amp; Outstation Travel approval workflow matrix · Manager &amp; Finance Review
            </p>
          </div>
        </div>

        <button onClick={fetchApprovalRequests} className="flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-xs font-bold text-white transition-all">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Module Filter Pills + Category Tabs + Search Bar */}
      <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-slate-100 pb-3">
          {/* Module Filter: All / Travel Outstation / Local Conveyance */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider mr-1">Module:</span>
            {[
              { id: 'all', label: 'All Requests' },
              { id: 'outstation', label: 'Travel / Outstation' },
              { id: 'local', label: 'Local Conveyance' },
            ].map((m) => (
              <button
                key={m.id}
                onClick={() => setModuleFilter(m.id)}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all ${
                  moduleFilter === m.id
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {/* Search Input */}
          <div className="relative w-full md:w-80">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
            <input
              type="text"
              placeholder="Search by Employee, Code, Department, Purpose..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium outline-none focus:border-blue-500"
            />
          </div>
        </div>

        {/* Status Category Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {['pending', 'advances', 'approved', 'rejected', 'returned', 'history'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-xl text-xs font-bold capitalize transition-all whitespace-nowrap ${
                activeTab === tab ? 'bg-blue-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {tab === 'advances' ? 'Finance Advances Queue' : `${tab} Requests`}
            </button>
          ))}
        </div>
      </div>

      {/* Item List */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4">
        {loading ? (
          <div className="p-8 text-center text-slate-500 font-medium">Loading approval inbox...</div>
        ) : filteredRequests.length === 0 ? (
          <div className="p-12 text-center text-slate-400 font-semibold italic">
            No {activeTab} requests found in inbox for module: <b>{moduleFilter.toUpperCase()}</b>.
          </div>
        ) : (
          filteredRequests.map((r) => {
            const isLocal = r.module_type === 'local';
            const itemId = r._id || r.id;
            const isProcessing = actionProcessing === itemId;
            const remarksValue = actionComments[itemId] || '';

            if (isLocal) {
              // Local Conveyance Card rendering
              const grandTotal = r.total_amount || r.totals?.grand_total || 0;
              const eligibleTotal = r.eligible_amount || r.totals?.eligible_total || grandTotal;

              const segments = r.segments || [];
              const routePoints = [];
              if (segments.length > 0 && segments[0].from_location) routePoints.push(segments[0].from_location);
              segments.forEach((s) => {
                if (s.to_location) routePoints.push(s.to_location);
              });
              if (!routePoints.length) {
                if (r.from_location) routePoints.push(r.from_location);
                if (r.to_location) routePoints.push(r.to_location);
              }
              const connectedRoute = routePoints.join(' → ') || 'Local Duty Route';

              const expDoc = r.expenses || {};
              const foodAmt = r.food_amount || expDoc.food_expense || 0;
              const parkingTollAmt = r.parking_toll_amount || (expDoc.parking || 0) + (expDoc.toll || 0);
              const conveyanceAmt = r.conveyance_amount || grandTotal - (foodAmt + parkingTollAmt + (r.other_amount || 0));
              const otherAmt = r.other_amount || expDoc.other_expense || 0;

              const approvals = r.approvals || [];
              const currIdx = r.current_level_index;

              return (
                <div key={r.unified_id} className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4 shadow-sm">
                  {/* Card Top Header */}
                  <div className="flex justify-between items-start flex-wrap gap-2">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] bg-blue-100 text-blue-900 font-bold px-2 py-0.5 rounded border border-blue-200 uppercase">
                          Local Conveyance
                        </span>
                        <span className="font-mono text-xs font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                          {r.report_number || r.report_id || `LC-${itemId.slice(-6).toUpperCase()}`}
                        </span>
                        <span className="text-xs text-slate-500 font-medium flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" /> Date: <b>{r.report_date}</b>
                        </span>
                      </div>
                      <h4 className="font-bold text-slate-900 text-base mt-1">
                        {r.employee_name} ({r.employee_code || 'EMP'}) — <span className="text-blue-800">{r.travel_purpose || 'Local Conveyance'}</span>
                      </h4>
                      <p className="text-xs text-slate-500">
                        Department: <b>{r.department_name || 'General'}</b> &bull; Designation: <b>{r.designation_name || 'Staff'}</b> &bull; Company: <b>{r.company_name || 'Main'}</b>
                      </p>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => handleOpenDetails(r)}
                        className="px-3 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-xs rounded-xl border border-indigo-200 flex items-center gap-1 transition-all"
                      >
                        <Eye className="w-3.5 h-3.5" /> View Full Details
                      </button>
                      <button
                        onClick={() => handleDownloadCardPdf(r)}
                        className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl border border-slate-300 flex items-center gap-1 transition-all"
                      >
                        <Download className="w-3.5 h-3.5" /> Download PDF
                      </button>
                      <span className="bg-amber-100 text-amber-900 text-xs font-bold px-3 py-1 rounded-full border border-amber-300">
                        Current Stage: {
                          approvals && currIdx !== null && currIdx !== undefined && approvals[currIdx]
                            ? (approvals[currIdx].level_name || approvals[currIdx].approver_role || `Level ${currIdx + 1}`)
                            : (r.approval_workflow_name || 'Review')
                        }
                      </span>
                    </div>
                  </div>

                  {/* Stepper Bar */}
                  {approvals.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 p-2.5 bg-white rounded-xl border border-slate-200 text-xs">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mr-1">Approval Levels:</span>
                      {approvals.map((lvl, idx) => {
                        const isDone = lvl.status === 'approved';
                        const isCurrent = currIdx === idx;
                        const isRejected = lvl.status === 'rejected';
                        return (
                          <span
                            key={idx}
                            className={`text-[10px] font-bold px-2 py-0.5 rounded border flex items-center gap-1 ${
                              isDone
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                                : isCurrent
                                ? 'bg-blue-100 text-blue-800 border-blue-400 font-extrabold'
                                : isRejected
                                ? 'bg-rose-50 text-rose-700 border-rose-300'
                                : 'bg-slate-100 text-slate-500 border-slate-200'
                            }`}
                          >
                            L{idx + 1}: {lvl.level_name || lvl.approver_role} {lvl.approver_name ? `(${lvl.approver_name})` : ''} {isDone ? '✓' : isRejected ? '✗' : ''}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {/* Route & KM */}
                  <div className="bg-white p-3 rounded-xl border border-slate-200 flex justify-between items-center flex-wrap gap-2 text-xs">
                    <span className="font-bold text-slate-800 flex items-center gap-1">
                      <MapPin className="w-4 h-4 text-blue-600" /> Route: <span className="text-blue-900">{connectedRoute}</span>
                    </span>
                    <span className="font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                      Total KM: {r.total_km || 0} KM
                    </span>
                  </div>

                  {/* Itemized 4 Expenses */}
                  <div className="bg-white p-3 rounded-xl border border-slate-200 text-xs space-y-2">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-slate-700">
                      <div><span className="text-[10px] text-slate-400 block">Conveyance:</span> <b>₹ {conveyanceAmt.toLocaleString('en-IN')}</b></div>
                      <div><span className="text-[10px] text-slate-400 block">Food &amp; Refreshment:</span> <b>₹ {foodAmt.toLocaleString('en-IN')}</b></div>
                      <div><span className="text-[10px] text-slate-400 block">Parking &amp; Toll:</span> <b>₹ {parkingTollAmt.toLocaleString('en-IN')}</b></div>
                      <div><span className="text-[10px] text-slate-400 block">Other:</span> <b>₹ {otherAmt.toLocaleString('en-IN')}</b></div>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                      <span className="font-bold text-slate-800">Grand Total Claim Amount</span>
                      <span className="text-base font-black text-blue-900 font-mono">₹ {grandTotal.toLocaleString('en-IN')}</span>
                    </div>
                  </div>

                  {/* Attachments */}
                  {(() => {
                    const inboxAtts = (r.attachments && r.attachments.length > 0)
                      ? r.attachments
                      : (r.self_booking_details?.all_attachments || []);

                    if (!inboxAtts || inboxAtts.length === 0) return null;

                    return (
                      <div className="flex items-center gap-2 flex-wrap text-xs bg-white p-2 rounded-xl border border-slate-200">
                        <Paperclip className="w-4 h-4 text-slate-400" />
                        <span className="font-bold text-slate-700">Attachments ({inboxAtts.length}):</span>
                        {inboxAtts.map((att, idx) => {
                          const isObj = att && typeof att === 'object';
                          const attName = isObj ? (att.name || att.filename || `Receipt #${idx + 1}`) : (typeof att === 'string' && att.trim() ? att.split('?')[0].split('/').pop() || `Receipt #${idx + 1}` : `Receipt #${idx + 1}`);

                          return (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => {
                                setPreviewAttachment(att);
                                setIsPreviewOpen(true);
                              }}
                              className="px-2 py-0.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded font-medium text-[11px] flex items-center gap-1 border border-blue-200 transition-colors"
                              title="View Attachment"
                            >
                              <FileText className="w-3 h-3 text-blue-600" />
                              <span>{attName}</span>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Action controls */}
                  {activeTab === 'pending' && (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-slate-200">
                      <input
                        type="text"
                        placeholder="Approver remarks / comments..."
                        value={remarksValue}
                        onChange={(e) => handleRemarkChange(itemId, e.target.value)}
                        className="h-9 px-3 bg-white border border-slate-300 rounded-xl text-xs w-full max-w-md outline-none focus:border-blue-500"
                        disabled={isProcessing}
                      />

                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => handleLocalAction(itemId, 'send-back')}
                          disabled={isProcessing}
                          className="px-3.5 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white font-bold text-xs rounded-xl transition-all"
                        >
                          Return
                        </button>
                        <button
                          onClick={() => handleLocalAction(itemId, 'reject')}
                          disabled={isProcessing}
                          className="px-4 py-2 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-bold text-xs rounded-xl transition-all"
                        >
                          Reject
                        </button>
                        <button
                          onClick={() => handleLocalAction(itemId, 'approve')}
                          disabled={isProcessing}
                          className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center gap-1"
                        >
                          <CheckCircle className="w-4 h-4" /> Approve Claim &rarr;
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            // Outstation Request Card rendering
            const isAdvPaid = r.advance_status === 'paid' || r.advance_payment?.payment_status === 'paid';
            const totalAdv = r.advance_details?.total_advance || 0;

            return (
              <div key={r.unified_id} className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] bg-indigo-100 text-indigo-900 font-bold px-2 py-0.5 rounded border border-indigo-200 uppercase">
                        Travel / Outstation
                      </span>
                      <span className="font-mono text-xs font-bold text-blue-600">{r.request_number}</span>
                      {r.parent_batch_id && (
                        <span className="text-[10px] bg-amber-100 text-amber-900 border border-amber-300 font-bold px-2 py-0.5 rounded-md font-mono">
                          Batch: #{r.parent_batch_id}
                        </span>
                      )}
                      {r.requested_on_behalf && (
                        <span className="text-[10px] bg-indigo-100 text-indigo-900 border border-indigo-300 font-bold px-2 py-0.5 rounded-md">
                          On-Behalf Request
                        </span>
                      )}
                    </div>
                    <h4 className="font-bold text-slate-900 text-base mt-0.5">
                      {r.beneficiary_name || r.employee_name} ({r.beneficiary_employee_code || r.employee_code || 'EMP'}) — {r.purpose}
                    </h4>
                    {r.requested_on_behalf && (
                      <p className="text-xs text-indigo-700 font-semibold">
                        Raised on-behalf by: {r.requester_name || r.created_by_name || 'Requester'}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => handleOpenDetails(r)}
                      className="px-3 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-xs rounded-xl border border-indigo-200 flex items-center gap-1 transition-all"
                    >
                      <Eye className="w-3.5 h-3.5" /> View Full Details
                    </button>
                    <button
                      onClick={() => handleDownloadCardPdf(r)}
                      className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl border border-slate-300 flex items-center gap-1 transition-all"
                    >
                      <Download className="w-3.5 h-3.5" /> Download PDF
                    </button>
                    {r.advance_requested && (
                      <span className={`text-xs font-bold px-3 py-1 rounded-full uppercase border ${
                        isAdvPaid ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'bg-purple-100 text-purple-900 border-purple-300'
                      }`}>
                        Advance: {isAdvPaid ? 'Paid' : r.advance_status?.replace(/_/g, ' ') || 'Pending'}
                      </span>
                    )}
                    <span className="bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1 rounded-full uppercase">
                      Awaiting: {
                        r.approvals && r.current_level_index !== null && r.current_level_index !== undefined && r.approvals[r.current_level_index]
                          ? (r.approvals[r.current_level_index].level_name || r.approvals[r.current_level_index].approver_role || `Level ${r.current_level_index + 1}`)
                          : (r.current_approval_step?.replace(/_/g, ' ') || 'Review')
                      }
                    </span>
                  </div>
                </div>

                {r.approvals && r.approvals.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 p-2.5 bg-white rounded-xl border border-slate-200">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mr-1">Approval Chain:</span>
                    {r.approvals.map((lvl, idx) => {
                      const isDone = lvl.status === 'approved';
                      const isCurrent = r.current_level_index === idx;
                      const isRejected = lvl.status === 'rejected';
                      return (
                        <span
                          key={idx}
                          className={`text-[10px] font-bold px-2 py-0.5 rounded border ${
                            isDone
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                              : isCurrent
                              ? 'bg-blue-100 text-blue-800 border-blue-400 font-extrabold'
                              : isRejected
                              ? 'bg-rose-50 text-rose-700 border-rose-300'
                              : 'bg-slate-100 text-slate-500 border-slate-200'
                          }`}
                        >
                          L{idx + 1}: {lvl.level_name || lvl.approver_role} {isDone ? '✓' : isRejected ? '✗' : ''}
                        </span>
                      );
                    })}
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs bg-white p-3.5 rounded-xl border border-slate-100 font-medium">
                  <div><b>Department:</b> {r.department_name || r.department || 'N/A'}</div>
                  <div><b>Project:</b> {r.project_name ? `${r.project_name} (${r.project_code || 'N/A'})` : (r.project || 'N/A')}</div>
                  <div><b>Travel Mode:</b> <span className="capitalize">{r.travel_type}</span></div>
                  <div><b>Route:</b> {r.journey_details?.[0]?.from_city || r.origin_city} &rarr; {r.journey_details?.[0]?.to_city || r.destination_city}</div>
                </div>

                {r.advance_requested && (
                  <div className="bg-amber-50/80 border border-amber-200 p-3.5 rounded-xl space-y-3 text-xs">
                    <div className="flex justify-between items-center font-bold text-amber-900 border-b border-amber-200/80 pb-2">
                      <span className="text-xs font-black uppercase tracking-wider text-amber-950">Travel Advance Requisition Breakdown</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-amber-100 text-amber-900 border border-amber-300 font-bold px-2.5 py-1 rounded-md">
                          Requested Advance: <b className="font-mono text-amber-950">₹ {(r.requested_advance_amount || r.advance_details?.requested_advance_amount || totalAdv).toLocaleString('en-IN')}</b>
                        </span>
                        {r.approved_advance_amount !== null && r.approved_advance_amount !== undefined && (
                          <span className="text-xs bg-emerald-100 text-emerald-900 border border-emerald-300 font-bold px-2.5 py-1 rounded-md">
                            Approved Advance: <b className="font-mono text-emerald-950">₹ {(r.approved_advance_amount).toLocaleString('en-IN')}</b>
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] text-amber-800 font-medium">
                      <div>Hotel: <b>₹ {(r.advance_details?.hotel_amount || 0).toLocaleString('en-IN')}</b></div>
                      <div>Food: <b>₹ {(r.advance_details?.food_amount || 0).toLocaleString('en-IN')}</b></div>
                      <div>Local Travel: <b>₹ {(r.advance_details?.local_travel_amount || 0).toLocaleString('en-IN')}</b></div>
                      <div>Misc: <b>₹ {(r.advance_details?.misc_amount || 0).toLocaleString('en-IN')}</b></div>
                    </div>

                    {activeTab === 'pending' && (
                      <div className="pt-2 border-t border-amber-200/80 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <label className="font-bold text-amber-950 text-xs shrink-0">
                          Approved Advance Amount (₹) *:
                        </label>
                        <div className="relative w-full max-w-xs">
                          <span className="absolute left-3 top-1.5 font-bold text-slate-500">₹</span>
                          <input
                            type="number"
                            min="0"
                            step="any"
                            placeholder="Enter Approved Amount"
                            value={
                              approvedAmounts[itemId] !== undefined
                                ? approvedAmounts[itemId]
                                : (r.approved_advance_amount ?? r.advance_details?.approved_advance_amount ?? (r.requested_advance_amount || r.advance_details?.requested_advance_amount || totalAdv))
                            }
                            onChange={(e) => handleApprovedAmountChange(itemId, e.target.value)}
                            className="w-full pl-7 pr-3 py-1.5 bg-white border border-amber-300 rounded-xl text-xs font-mono font-bold text-slate-900 outline-none focus:border-blue-500 shadow-sm"
                            disabled={isProcessing}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Controls for Outstation */}
                {activeTab === 'pending' && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-slate-200">
                    <input
                      type="text"
                      placeholder="Approver remarks / comments..."
                      value={remarksValue}
                      onChange={(e) => handleRemarkChange(itemId, e.target.value)}
                      className="h-9 px-3 bg-white border border-slate-300 rounded-xl text-xs w-full max-w-md outline-none"
                      disabled={isProcessing}
                    />

                    {hasPermission(user, 'travel_desk.approval.approve') ? (
                      <div className="flex gap-2 shrink-0">
                        <button
                          onClick={() => handleOutstationAction(itemId, 'reject', r)}
                          disabled={isProcessing}
                          className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs rounded-xl"
                        >
                          Reject
                        </button>
                        <button
                          onClick={() => handleOutstationAction(itemId, 'approve', r)}
                          disabled={isProcessing}
                          className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs rounded-xl shadow-md"
                        >
                          Approve Request &rarr;
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400 font-semibold italic">View Only (Approve permission required)</span>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Travel Request Full Details & PDF Modal */}
      <TravelRequestDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        requestId={selectedRequestId}
        moduleType={selectedModuleType}
        onActionCompleted={fetchApprovalRequests}
        currentUser={user}
      />

      {/* Attachment Preview Modal */}
      <AttachmentPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        attachment={previewAttachment}
        title="Receipt &amp; Bill Preview"
      />
    </div>
  );
};

export default TravelApprovalInbox;
